import { useEffect, useRef, useState } from "react";
import Joyride, { STATUS, CallBackProps, Step } from "react-joyride";
import { createApiService } from "../shared/apiConfig";
import {
  ServerRequestsGetCompletedTutorialResponse,
  TutorialProgressApi,
} from "../shared/api/axios-client";
import { toast } from "react-toastify";
import { useAtom } from "jotai";
import { authAtom } from "../Data/Atoms";

export type JoyrideTourProps = {
  /** Filename base inside `public/steps`. e.g., `"dashboard"` -> `/steps/dashboard.json` */
  tourKey: string;
  /** Optional prefix used for the localStorage key */
  storagePrefix?: string;
  /** Start automatically once steps are loaded (default: true) */
  autoStart?: boolean;
};

export default function TourGuide({
  tourKey,
  autoStart = true,
}: JoyrideTourProps) {
  const [steps, setSteps] = useState<Step[]>([]);
  const [run, setRun] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [completedTutorials, setCompletedTutorials] =
    useState<ServerRequestsGetCompletedTutorialResponse>();
  const [auth] = useAtom(authAtom);
  const staffId = auth.account!.staffId;

  const tutorialsProgressApi = createApiService(TutorialProgressApi);

  useEffect(() => {
    const fetchCompletedTutorials = async () => {
      try {
        const response =
          await tutorialsProgressApi.tutorialProgressCompletedStaffIdGet({
            staffId,
          });
        setCompletedTutorials(response.data);
      } catch (err) {
        console.error("Failed to fetch tutorial progress", err);
      }
    };

    fetchCompletedTutorials();
  }, [staffId]);

  useEffect(() => {
    let isMounted = true;

    if (!completedTutorials) return;

    if (completedTutorials.completedTutorials?.includes(tourKey)) {
      setRun(false);
      setLoading(false);
      return;
    }

    const waitForTargets = async (
      targets: string[],
      retries = 20,
      delay = 250
    ): Promise<boolean> => {
      const missing = targets.filter((t) => !document.querySelector(t));
      if (missing.length === 0) return true;
      if (retries <= 0) return false;

      await new Promise((r) => setTimeout(r, delay));
      return waitForTargets(targets, retries - 1, delay);
    };

    const loadSteps = async () => {
      try {
        const res = await fetch(`/steps/${tourKey}.json`);
        if (!res.ok) throw new Error(`Failed to fetch steps: ${res.status}`);
        const data = (await res.json()) as Step[];

        if (!Array.isArray(data))
          throw new Error("Steps JSON must be an array of steps.");

        if (!isMounted) return;

        const forcedSteps = data.map((s) => ({ ...s, disableBeacon: true }));
        setSteps(forcedSteps);

        const targets = forcedSteps.map((s) => s.target as string);
        const ready = await waitForTargets(targets);

        if (ready && isMounted) {
          setRun(Boolean(autoStart) && forcedSteps.length > 0);
        }
      } catch (e: any) {
        if (isMounted) setError(e.message ?? String(e));
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    loadSteps();

    return () => {
      isMounted = false;
    };
  }, [tourKey, autoStart, completedTutorials]);

  const hasCompletedRef = useRef(false);

  const handleJoyrideCallback = async (data: CallBackProps) => {
    const { status, action } = data;
    const finished =
      status === STATUS.FINISHED ||
      status === STATUS.SKIPPED ||
      action === "close";

    if (!finished || hasCompletedRef.current) return;
    hasCompletedRef.current = true;

    try {
      await tutorialsProgressApi.tutorialProgressPost({
        serverRequestsSaveTutorialProgressRequest: {
          staffId,
          tutorialKey: tourKey,
        },
      });

      if (finished) {
        toast.success("Tutorial completed!");
        setRun(false);
      }
    } catch {
      toast.error("Failed to save tutorial progress.");
    }
  };

  if (loading || error) return null; // Keep UI clean if still loading or error fetching steps
  if (!run || steps.length === 0) return null;

  return (
    <Joyride
      {...({ disableBeacon: true } as any)}
      steps={steps}
      run={run}
      disableCloseOnEsc={false}
      disableOverlayClose={false}
      continuous
      showSkipButton
      showCloseButton
      showProgress
      callback={handleJoyrideCallback}
      styles={{
        options: {
          zIndex: 9999,
        },
      }}
      locale={{
        last: "Finish",
      }}
    />
  );
}
